/**
 * The portrait dispatch order as an image — 1080 px wide, taller than it is
 * wide, sized for a phone screen and for the WhatsApp preview it lands in.
 *
 * Two halves, deliberately split:
 *
 *  1. {@link layoutDispatchPage} — pure. It turns one page of a
 *     {@link DispatchCard} into a flat list of rectangles and text runs with
 *     absolute coordinates. No DOM, no canvas, no fonts: the healthcheck lays
 *     out the very same order in Node and asserts on what would be drawn. A
 *     convoy longer than a phone screen can hold becomes several pages —
 *     {@link layoutDispatchPages}.
 *  2. {@link paintDispatchCard} — the only part that talks to a canvas, and it
 *     makes no decisions: it walks the list and draws it.
 *
 * Text width is estimated rather than measured, for the same reason: a layout
 * that asks the canvas how wide a word is could not be verified without a
 * browser. The estimate is deliberately generous, so a long Hebrew line wraps
 * early rather than running off the card.
 */
import {
  DISPATCH_CARD_SECTION_TITLES,
  joinParts,
  paginateDispatchCard,
  singleDispatchPage,
} from './dispatch-card';
import type { DispatchCard, DispatchCardPage, DispatchCardVehicle } from './dispatch-card';
import { crewPrimaryLine, crewSecondaryLine } from './dispatch-report';
import { EXTERNAL_PERSON_TAG, TEMPORARY_VEHICLE_LABEL } from '../types/assignment';

// ── The page ─────────────────────────────────────────────────────────

/** Mandated width of the shared image. */
export const DISPATCH_IMAGE_WIDTH = 1080;

/** The shortest the image may be — 9:16, so it is never a landscape strip. */
export const DISPATCH_IMAGE_MIN_HEIGHT = Math.round((DISPATCH_IMAGE_WIDTH * 16) / 9);

export const DISPATCH_IMAGE_PADDING = 48;

// ── Filling the frame ────────────────────────────────────────────────

/**
 * The order is laid out to *fill* the 1080×1920 frame, not merely to fit in it.
 *
 * A mission with one vehicle carries perhaps half the lines of a mission with
 * three, and drawn at one fixed size it left the bottom third of the image
 * blank — a dispatch order that looks truncated and reads small on a phone. So
 * the layout takes two factors:
 *
 *  - `type` — every font size and every box measured in ems. It is stretched
 *    first, and only so far: past ~1.4 a title starts wrapping into three lines
 *    and the order stops reading like one.
 *  - `gap` — the vertical air between the blocks, and the side margins with it.
 *    Whitespace absorbs whatever slack the type could not, which is why it may
 *    stretch much further: air can grow without anything breaking.
 *
 * Both may also *shrink*, down to the floors below, so a dense three-vehicle
 * order squeezes onto one page rather than spilling into a taller image. The
 * type floor is what keeps the mandated invariants true — at 0.85 the internal
 * number is still drawn at 41px, larger than anything else on a convoy line.
 */
export const DISPATCH_TYPE_SCALE = { min: 0.85, max: 1.4 } as const;

/**
 * How far the whitespace may be stretched or squeezed. Far wider than the type
 * range on purpose: the sparsest page an order can produce is the last part of
 * a convoy — one vehicle, no mission block — and air is the only thing that can
 * fill it without making a driver's name look like a headline.
 */
export const DISPATCH_GAP_SCALE = { min: 0.85, max: 4.5 } as const;

/** The two factors one page is drawn at. */
export interface DispatchScale {
  type: number;
  gap: number;
}

/** Neither stretched nor squeezed — the reference layout. */
export const DISPATCH_SCALE_NEUTRAL: DispatchScale = { type: 1, gap: 1 };

const clampTo = (value: number, range: { min: number; max: number }): number =>
  Math.min(range.max, Math.max(range.min, Number.isFinite(value) ? value : 1));

/** Three decimals — enough to land on the frame, few enough to stay stable. */
const round3 = (value: number): number => Math.round(value * 1000) / 1000;

/**
 * The card colours, in one place — the canvas cannot read a Tailwind class.
 *
 * A high-end tactical register rather than a brand one: a deep navy command
 * band closed by a metallic rule, near-black type on white panels lifted off a
 * quiet ground, and the internal number and the plate at the highest contrast
 * on the page, because those are the two things a driver checks at a glance in
 * a lit yard at night. The screen card (`DispatchOrderCard.tsx`) is painted from
 * the same values, so the PNG and the dialog are one design and not two.
 */
export const DISPATCH_IMAGE_COLORS = {
  paper: '#ffffff',
  canvas: '#eef2f7',
  /** The soft drop under a panel — a lift, never a border. */
  shadow: '#dce3ec',
  ink: '#0b1220',
  muted: '#64748b',
  /** border-slate-200, the one edge every panel is drawn with. */
  line: '#e2e8f0',
  rule: '#e2e8f0',
  /** The tactical command band — slate-900. */
  navy: '#0f172a',
  navyInk: '#ffffff',
  navySub: '#94a3b8',
  /** The metallic rule that closes the band. */
  metal: '#94a3b8',
  /** A metadata chip on the band: a shade up from it, ruled a shade above that. */
  chip: '#1e293b',
  chipLine: '#475569',
  accent: '#334155',
  idBox: '#f1f5f9',
  /**
   * The mark an off-roster person is drawn with — violet, used by nothing else
   * on the order. A reader has to be able to tell in one glance which of the
   * names on a convoy line is the unit's own and which was attached for the day,
   * so the distinction is a filled tag and a tinted block rather than a word in
   * brackets that a photographed order loses.
   */
  external: '#5b21b6',
  externalInk: '#ffffff',
  externalWash: '#f5f3ff',
  externalLine: '#c4b5fd',
} as const;

export type DispatchTextWeight = 'regular' | 'bold' | 'black';

export const DISPATCH_FONT_WEIGHTS: Record<DispatchTextWeight, number> = {
  regular: 400,
  bold: 700,
  black: 900,
};

/** The font stack the image is drawn with — the application's own. */
export const DISPATCH_IMAGE_FONT_FAMILY = '"Assistant", "Rubik", "Arial", sans-serif';

/** The banner line above the task title. */
export const DISPATCH_CARD_TITLE_LINE = 'פקודת משימה · משימות שינוע 99';

/** The line the image signs off with. */
export const DISPATCH_IMAGE_FOOTER = 'הופק ממערכת משימות שינוע 99 · לשימוש פנימי ביחידה בלבד';

export interface DispatchRectOp {
  kind: 'rect';
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  fill?: string;
  stroke?: string;
}

export interface DispatchTextOp {
  kind: 'text';
  x: number;
  y: number;
  text: string;
  size: number;
  weight: DispatchTextWeight;
  color: string;
  align: 'right' | 'left' | 'center';
  /** Numbers — a plate, a phone, a clock — are read left to right even here. */
  ltr?: boolean;
}

export type DispatchImageOp = DispatchRectOp | DispatchTextOp;

export interface DispatchImageLayout {
  width: number;
  height: number;
  /**
   * Where the order actually ends, before the frame is padded out to its
   * minimum. `height - contentHeight` is the blank strip at the bottom, and
   * driving that to nearly nothing is the whole point of {@link fitDispatchScale}.
   */
  contentHeight: number;
  /** The two factors this page was drawn at. */
  scale: DispatchScale;
  ops: DispatchImageOp[];
}

/**
 * Roughly how wide a run of text is at a given size. Hebrew glyphs in the
 * application's font sit a little under 0.55em; the figure is rounded up so the
 * estimate errs towards wrapping.
 */
export function measureTextWidth(text: string, size: number): number {
  return Math.ceil(String(text ?? '').length * size * 0.55);
}

/** Breaks a line on word boundaries so it fits `maxWidth`, never mid-word. */
export function wrapText(text: string, size: number, maxWidth: number): string[] {
  const words = String(text ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && measureTextWidth(candidate, size) > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

// ── The layout ───────────────────────────────────────────────────────

/**
 * Lays one page of the order out top to bottom, at a given pair of scale
 * factors. RTL: every text run is anchored to the right edge of its box, which
 * is why nearly every `x` below is a right edge and the few LTR runs (numbers,
 * plates, phones) are centred or anchored on the left.
 *
 * A page holds at most {@link DISPATCH_PAGE_VEHICLES} vehicles. The first page
 * carries the mission — times, destination, notes — and every later page opens
 * on a compact header and goes straight to the next slice of the convoy.
 *
 * Two helpers carry the whole adaptive layout: `t()` scales type and anything
 * measured in ems, `g()` scales vertical air. Every literal below goes through
 * one of the two, so a page has exactly two degrees of freedom and the fit can
 * be solved for rather than guessed at.
 */
function layoutAt(page: DispatchCardPage, scale: DispatchScale): DispatchImageLayout {
  const card = page.card;
  const width = DISPATCH_IMAGE_WIDTH;
  /** Type, boxes, rules — everything that scales with the letterform. */
  const t = (value: number) => Math.max(1, Math.round(value * scale.type));
  /** Vertical air between and inside the blocks. */
  const g = (value: number) => Math.round(value * scale.gap);
  // The side margins breathe with the air, but only so far: the card still has
  // to hold a boxed internal number and a model name side by side.
  const pad = Math.round(DISPATCH_IMAGE_PADDING * Math.min(scale.gap, 1.6));
  const right = width - pad;
  const left = pad;
  const contentWidth = width - pad * 2;
  // Leading opens up with the air too — the mandated "line spacing" — but a
  // paragraph is still a paragraph, so it stops well short of double-spaced.
  const leading = 1.25 + Math.min(Math.max(scale.gap - 1, 0), 1) * 0.15;
  const ops: DispatchImageOp[] = [];
  let y = 0;

  const rect = (op: Omit<DispatchRectOp, 'kind'>) => ops.push({ kind: 'rect', ...op });
  const line = (
    value: string,
    size: number,
    weight: DispatchTextWeight,
    color: string,
    options: { x?: number; align?: 'right' | 'left' | 'center'; ltr?: boolean; maxWidth?: number } = {},
  ) => {
    const maxWidth = options.maxWidth ?? contentWidth - 32;
    for (const part of wrapText(value, size, maxWidth)) {
      y += Math.round(size * leading);
      ops.push({
        kind: 'text',
        x: options.x ?? right - 16,
        y,
        text: part,
        size,
        weight,
        color,
        align: options.align ?? 'right',
        ltr: options.ltr,
      });
    }
  };

  /**
   * The metadata chips of the command band — the type, the status, the size of
   * the convoy. Laid out right to left, each one sized to the words in it, and
   * a chip that would run off the band is dropped rather than clipped.
   */
  const chipRow = (labels: readonly (string | null | undefined)[], size: number): void => {
    const parts = labels.map((value) => String(value ?? '').trim()).filter(Boolean);
    if (parts.length === 0) return;
    const height = Math.round(size * 1.9);
    const top = y;
    let cursor = right;
    for (const label of parts) {
      const chipWidth = measureTextWidth(label, size) + Math.round(size * 1.4);
      if (cursor - chipWidth < left) break;
      rect({
        x: cursor - chipWidth,
        y: top,
        width: chipWidth,
        height,
        radius: Math.round(size * 0.5),
        fill: DISPATCH_IMAGE_COLORS.chip,
        stroke: DISPATCH_IMAGE_COLORS.chipLine,
      });
      ops.push({
        kind: 'text',
        x: cursor - Math.round(chipWidth / 2),
        y: top + Math.round(height * 0.7),
        text: label,
        size,
        weight: 'bold',
        color: DISPATCH_IMAGE_COLORS.navySub,
        align: 'center',
      });
      cursor -= chipWidth + Math.round(size * 0.45);
    }
    y = top + height;
  };

  // ── The header band: what, of which kind, and which part of the order ──
  const headerStart = y;
  y += g(10);
  line(page.pageLabel || DISPATCH_CARD_TITLE_LINE, t(26), 'bold', DISPATCH_IMAGE_COLORS.navySub, {
    x: right,
  });
  y += g(8);
  line(card.title, t(page.detailed ? 52 : 40), 'black', DISPATCH_IMAGE_COLORS.navyInk, {
    x: right,
    maxWidth: contentWidth,
  });
  y += g(14);
  chipRow(
    page.detailed
      ? [card.typeName, card.statusLabel, convoySlice(page)]
      : [card.dateLabel, card.window, convoySlice(page)],
    t(24),
  );
  y += g(26);
  const headerHeight = Math.max(t(page.detailed ? 230 : 190), y - headerStart);
  ops.unshift({
    kind: 'rect',
    x: 0,
    y: 0,
    width,
    height: headerHeight,
    radius: 0,
    fill: DISPATCH_IMAGE_COLORS.navy,
  });
  // The metallic rule that closes the band — the card's one hard edge.
  const bandRule = t(8);
  ops.push({
    kind: 'rect',
    x: 0,
    y: headerHeight,
    width,
    height: bandRule,
    radius: 0,
    fill: DISPATCH_IMAGE_COLORS.metal,
  });
  y = headerHeight + bandRule + g(32);

  /**
   * A plate drawn as a licence tag: white on near-black, sized to the plate it
   * carries and never wider than the column it was given. The one object on the
   * order that is legible from across a lit yard — which is why a temporary
   * vehicle's plate is drawn with exactly the same helper as a fleet vehicle's.
   */
  const plateTag = (value: string, rightEdge: number, size: number, maxWidth: number): void => {
    const tagWidth = Math.min(maxWidth, measureTextWidth(value, size) + Math.round(size * 1.6));
    const tagHeight = Math.round(size * 1.75);
    const tagTop = y;
    rect({
      x: rightEdge - tagWidth,
      y: tagTop,
      width: tagWidth,
      height: tagHeight,
      radius: 10,
      fill: DISPATCH_IMAGE_COLORS.ink,
    });
    ops.push({
      kind: 'text',
      x: rightEdge - Math.round(tagWidth / 2),
      y: tagTop + Math.round(tagHeight * 0.7),
      text: value,
      size,
      weight: 'black',
      color: DISPATCH_IMAGE_COLORS.paper,
      align: 'center',
      ltr: true,
    });
    y = tagTop + tagHeight;
  };

  /**
   * The "איש חוץ" mark, set on the same baseline as the name that follows it and
   * to the right of it — so an RTL reader meets the mark first and the name
   * second. Returns the width it took, which the caller subtracts from the
   * name's own right edge.
   */
  const externalMark = (baseline: number, rightEdge: number, size: number): number => {
    const label = EXTERNAL_PERSON_TAG;
    const tagWidth = measureTextWidth(label, size) + Math.round(size * 1.5);
    const tagHeight = Math.round(size * 1.8);
    const tagTop = baseline - Math.round(tagHeight * 0.78);
    rect({
      x: rightEdge - tagWidth,
      y: tagTop,
      width: tagWidth,
      height: tagHeight,
      radius: Math.round(size * 0.5),
      fill: DISPATCH_IMAGE_COLORS.external,
    });
    ops.push({
      kind: 'text',
      x: rightEdge - Math.round(tagWidth / 2),
      y: tagTop + Math.round(tagHeight * 0.72),
      text: label,
      size,
      weight: 'bold',
      color: DISPATCH_IMAGE_COLORS.externalInk,
      align: 'center',
    });
    return tagWidth + Math.round(size * 0.55);
  };

  /** A hairline the width of a panel — the card divides, it does not decorate. */
  const divider = (inset = 16) => {
    y += g(14);
    rect({
      x: left + inset,
      y,
      width: contentWidth - inset * 2,
      height: 2,
      radius: 0,
      fill: DISPATCH_IMAGE_COLORS.rule,
    });
    y += g(14);
  };

  /** One block of the order: a titled panel with its own lines inside it. */
  const section = (title: string, draw: () => void) => {
    const top = y;
    y += g(16);
    line(title, t(26), 'bold', DISPATCH_IMAGE_COLORS.accent, { x: right - 20 });
    y += g(4);
    rect({
      x: left + 20,
      y,
      width: contentWidth - 40,
      height: 2,
      radius: 0,
      fill: DISPATCH_IMAGE_COLORS.rule,
    });
    y += g(8);
    draw();
    y += g(22);
    // The panel and the soft drop it sits on, both slipped in behind the lines
    // that were laid out while the block was being measured. Shadow first, so
    // the white panel covers all but the sliver that shows under its edge.
    ops.splice(sectionInsertIndex(ops, top), 0, {
      kind: 'rect',
      x: left,
      y: top + 4,
      width: contentWidth,
      height: y - top,
      radius: 16,
      fill: DISPATCH_IMAGE_COLORS.shadow,
    }, {
      kind: 'rect',
      x: left,
      y: top,
      width: contentWidth,
      height: y - top,
      radius: 16,
      fill: DISPATCH_IMAGE_COLORS.paper,
      stroke: DISPATCH_IMAGE_COLORS.line,
    });
    y += g(22);
  };

  // ── The mission: only ever on the first page ───────────────────────
  if (page.detailed) {
    section(DISPATCH_CARD_SECTION_TITLES.times, () => {
      line(`תאריך: ${card.dateLabel}`, t(34), 'bold', DISPATCH_IMAGE_COLORS.ink);
      if (card.window) {
        line(`שעות: ${card.window}`, t(34), 'bold', DISPATCH_IMAGE_COLORS.ink);
      }
    });

    section(DISPATCH_CARD_SECTION_TITLES.destination, () => {
      line(`יעד: ${card.destination || '—'}`, t(32), 'bold', DISPATCH_IMAGE_COLORS.ink);
      if (card.contactName) {
        line(
          `איש קשר: ${joinParts([card.contactName, card.contactRole])}`,
          t(30),
          'regular',
          DISPATCH_IMAGE_COLORS.ink,
        );
      }
      if (card.contactPhone) {
        line(card.contactPhone, t(32), 'bold', DISPATCH_IMAGE_COLORS.ink, { x: right - 20 });
      }
      if (card.routeHref) {
        line('קו ניווט מצורף להודעה', t(26), 'regular', DISPATCH_IMAGE_COLORS.muted);
      }
    });
  }

  /**
   * One vehicle: the internal number boxed at the highest contrast on the page,
   * the model and the plate beside it, and under them only the seats that were
   * actually filled.
   */
  const vehicleBlock = (vehicle: DispatchCardVehicle): void => {
    const top = y;
    const boxWidth = t(176);
    const boxHeight = t(78);
    const boxTop = top + g(8);
    rect({
      x: right - 20 - boxWidth,
      y: boxTop,
      width: boxWidth,
      height: boxHeight,
      radius: 12,
      fill: DISPATCH_IMAGE_COLORS.idBox,
      stroke: DISPATCH_IMAGE_COLORS.ink,
    });
    ops.push({
      kind: 'text',
      x: right - 20 - boxWidth / 2,
      y: boxTop + Math.round(boxHeight * 0.72),
      text: vehicle.callSign,
      size: t(48),
      weight: 'black',
      color: DISPATCH_IMAGE_COLORS.ink,
      align: 'center',
      ltr: true,
    });

    const identityRight = right - 20 - boxWidth - 24;
    const identityWidth = identityRight - (left + 24);
    y = top + g(4);
    line(joinParts([vehicle.model || vehicle.name, vehicle.typeLabel]), t(32), 'bold', DISPATCH_IMAGE_COLORS.ink, {
      x: identityRight,
      maxWidth: identityWidth,
    });
    // The plate as a licence tag: white on near-black, the one object on the
    // order that is legible from across a yard. Sized to the plate it carries,
    // and never allowed past the width the identity column was given.
    if (vehicle.plateNumber) {
      y += g(10);
      plateTag(vehicle.plateNumber, identityRight, t(28), identityWidth);
    }
    if (vehicle.company) {
      line(vehicle.company, t(24), 'regular', DISPATCH_IMAGE_COLORS.muted, {
        x: identityRight,
        maxWidth: identityWidth,
      });
    }
    y = Math.max(y, boxTop + boxHeight + g(16));

    // Only assigned roles are drawn — a vehicle with no escort has no escort row.
    for (const member of vehicle.crew) {
      // Where this rider's own ops start, so an external one can have its tinted
      // block slipped in *behind* every line of it in one go.
      const entryAt = ops.length;
      const entryTop = y;
      y += g(8);

      // תפקיד: שם מלא — the name is the line, set at the crew's largest weight.
      // An external rider is marked before it, on the same baseline: the tag is
      // the first thing read on the line and cannot be lost to a re-share.
      const nameSize = t(30);
      let nameRight = right - 28;
      if (member.external) {
        nameRight -= externalMark(y + Math.round(nameSize * leading), nameRight, t(21));
      }
      line(crewPrimaryLine(member.roleLabel, member), nameSize, 'bold', DISPATCH_IMAGE_COLORS.ink, {
        x: nameRight,
        maxWidth: nameRight - (left + 28),
      });

      // 052-406-3968 · מ.א. 7533454 — the numbers, quieter and a size down. The
      // phone is what gets dialled off a photographed order, so it leads and is
      // drawn in the card's own ink; the מ.א. follows it, and neither is ever
      // allowed to wrap a driver's name onto a second line again.
      const numbers = crewSecondaryLine(member);
      if (numbers) {
        line(numbers, t(26), 'bold', DISPATCH_IMAGE_COLORS.accent, { x: right - 28 });
      }
      const details = joinParts([
        member.jobLabel,
        member.licenseLabel ? `רישיון ${member.licenseLabel}` : '',
      ]);
      if (details) {
        line(details, t(24), 'regular', DISPATCH_IMAGE_COLORS.muted, { x: right - 28 });
      }

      // The vehicle an external rider arrived in. It is not a fleet record and
      // has no מספר פנימי, so it is drawn as what it is: a labelled line and,
      // where a plate was given, the same licence tag a fleet vehicle gets.
      if (member.temporaryVehicleModel || member.temporaryPlateNumber) {
        const tempSize = t(24);
        y += g(6);
        line(
          joinParts([TEMPORARY_VEHICLE_LABEL, member.temporaryVehicleModel], ': '),
          tempSize,
          'bold',
          DISPATCH_IMAGE_COLORS.external,
          { x: right - 40, maxWidth: contentWidth - 80 },
        );
        if (member.temporaryPlateNumber) {
          y += g(6);
          plateTag(member.temporaryPlateNumber, right - 40, tempSize, contentWidth - 80);
        }
      }

      if (member.external) {
        y += g(8);
        ops.splice(entryAt, 0, {
          kind: 'rect',
          x: left + 24,
          y: entryTop + g(4),
          width: contentWidth - 48,
          height: Math.max(t(20), y - entryTop - g(4)),
          radius: 14,
          fill: DISPATCH_IMAGE_COLORS.externalWash,
          stroke: DISPATCH_IMAGE_COLORS.externalLine,
        });
      }
    }
    if (vehicle.note) {
      y += g(8);
      line(`הערת צוות: ${vehicle.note}`, t(24), 'regular', DISPATCH_IMAGE_COLORS.muted, { x: right - 28 });
    }
  };

  // ── The convoy, with each vehicle's own crew under it ──────────────
  section(`${DISPATCH_CARD_SECTION_TITLES.vehicles} · ${convoySlice(page)}`, () => {
    if (card.vehicles.length === 0) {
      line('טרם שובצו רכבים למשימה', t(28), 'regular', DISPATCH_IMAGE_COLORS.muted);
      return;
    }
    card.vehicles.forEach((vehicle, index) => {
      if (index > 0) divider(28);
      vehicleBlock(vehicle);
    });
  });

  // ── Operational notes: with the mission, on the first page ─────────
  if (page.detailed && card.notes) {
    section(DISPATCH_CARD_SECTION_TITLES.notes, () => {
      line(card.notes, t(28), 'regular', DISPATCH_IMAGE_COLORS.ink);
    });
  }

  y += g(16);
  line(
    joinParts([DISPATCH_IMAGE_FOOTER, page.pageLabel]),
    t(24),
    'regular',
    DISPATCH_IMAGE_COLORS.muted,
    { x: width / 2, align: 'center' },
  );

  const contentHeight = y + pad;
  const height = Math.max(DISPATCH_IMAGE_MIN_HEIGHT, contentHeight);
  ops.unshift({
    kind: 'rect',
    x: 0,
    y: 0,
    width,
    height,
    radius: 0,
    fill: DISPATCH_IMAGE_COLORS.canvas,
  });

  return { width, height, contentHeight, scale, ops };
}

/**
 * The two factors that make this page end at the bottom of the frame rather
 * than somewhere in the middle of it.
 *
 * Solved, not searched. Type scales the whole page uniformly, so the page
 * height is proportional to it and one division gives the answer. Air scales
 * only part of the height — `A + gap·B` — so two probes give the line and the
 * answer is read off it. Each is followed by a single correction pass, which
 * absorbs the rounding the layout does and the two floors (the header band, a
 * vehicle's boxed number) that are not perfectly proportional.
 *
 * A page that overflows the frame even at the floors is left overflowing: the
 * image simply grows taller. Legibility is the constraint that does not bend.
 */
export function fitDispatchScale(page: DispatchCardPage): DispatchScale {
  const target = DISPATCH_IMAGE_MIN_HEIGHT;
  const heightAt = (type: number, gap: number) => layoutAt(page, { type, gap }).contentHeight || target;

  // 1. The type, stretched uniformly with the air it sits in.
  let type = clampTo(target / heightAt(1, 1), DISPATCH_TYPE_SCALE);
  type = round3(clampTo((type * target) / heightAt(type, type), DISPATCH_TYPE_SCALE));

  // 2. The air, taking up whatever slack the type was not allowed to.
  const atOne = heightAt(type, 1);
  const atTwo = heightAt(type, 2);
  const perUnit = atTwo - atOne;
  if (perUnit <= 0) return { type, gap: clampTo(type, DISPATCH_GAP_SCALE) };

  /**
   * Height is not perfectly continuous in the scale — a line that wraps adds a
   * whole line at once — so the solved answer is corrected a few times and the
   * best of the candidates kept. Running past the frame is the worse of the two
   * misses: blank space at the bottom is merely ugly, a page that overruns the
   * fold is a page a driver has to scroll.
   */
  const miss = (height: number) => (height > target ? (height - target) * 2 : target - height);

  let gap = clampTo(1 + (target - atOne) / perUnit, DISPATCH_GAP_SCALE);
  let best = gap;
  let bestMiss = miss(heightAt(type, gap));
  for (let pass = 0; pass < 4 && bestMiss > 0; pass += 1) {
    gap = clampTo(gap + (target - heightAt(type, gap)) / perUnit, DISPATCH_GAP_SCALE);
    const missed = miss(heightAt(type, gap));
    if (missed >= bestMiss) break;
    best = gap;
    bestMiss = missed;
  }

  return { type, gap: round3(best) };
}

/**
 * One page of the order as an image. Without a `scale` the page is fitted to
 * the frame — see {@link fitDispatchScale}; passing one lays the page out at
 * exactly those factors, which is what the fitter itself and a test need.
 */
export function layoutDispatchPage(
  page: DispatchCardPage,
  scale?: DispatchScale,
): DispatchImageLayout {
  return layoutAt(page, scale ?? fitDispatchScale(page));
}

/**
 * Where a section's panel has to be inserted so it sits *behind* the lines that
 * were pushed while it was being laid out — the first op drawn at or after the
 * panel's own top.
 */
function sectionInsertIndex(ops: readonly DispatchImageOp[], top: number): number {
  const index = ops.findIndex((op) => op.kind === 'text' && op.y > top);
  return index === -1 ? ops.length : index;
}

/** `רכבים 4-6 מתוך 7`, or `סה"כ 3` when the whole convoy is on one page. */
function convoySlice(page: DispatchCardPage): string {
  const shown = page.card.vehicles.length;
  const total = page.card.vehicleCount || shown;
  if (page.pageCount === 1 || shown === 0) return `סה"כ ${shown}`;
  const last = page.firstVehicle + shown - 1;
  const span = shown === 1 ? `${page.firstVehicle}` : `${page.firstVehicle}-${last}`;
  return `רכבים ${span} מתוך ${total}`;
}

/**
 * The order as a single image — a preview, and what a convoy of three or fewer
 * is shared as. A longer convoy goes out through {@link layoutDispatchPages}.
 */
export function layoutDispatchCard(card: DispatchCard): DispatchImageLayout {
  return layoutDispatchPage(singleDispatchPage(card));
}

/**
 * Every image the order will be shared as, in reading order: one for a short
 * convoy, several for a long one.
 */
export function layoutDispatchPages(card: DispatchCard): DispatchImageLayout[] {
  // Each part is fitted to the frame on its own — `map`'s index must not reach
  // the optional scale argument, so the callback is spelled out.
  return paginateDispatchCard(card).map((page) => layoutDispatchPage(page));
}

// ── Painting ─────────────────────────────────────────────────────────

/** The CSS font shorthand for one text run. */
export function fontOf(op: DispatchTextOp): string {
  return `${DISPATCH_FONT_WEIGHTS[op.weight]} ${op.size}px ${DISPATCH_IMAGE_FONT_FAMILY}`;
}

/** A rounded rectangle path, without relying on `ctx.roundRect()`. */
function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/** Draws a laid-out order onto a canvas context. Decides nothing itself. */
export function paintDispatchCard(ctx: CanvasRenderingContext2D, layout: DispatchImageLayout): void {
  ctx.textBaseline = 'alphabetic';
  for (const op of layout.ops) {
    if (op.kind === 'rect') {
      roundedPath(ctx, op.x, op.y, op.width, op.height, op.radius);
      if (op.fill) {
        ctx.fillStyle = op.fill;
        ctx.fill();
      }
      if (op.stroke) {
        ctx.strokeStyle = op.stroke;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      continue;
    }
    ctx.font = fontOf(op);
    ctx.fillStyle = op.color;
    ctx.textAlign = op.align;
    ctx.direction = op.ltr ? 'ltr' : 'rtl';
    ctx.fillText(op.text, op.x, op.y);
  }
}

// ── The browser half ─────────────────────────────────────────────────

/** Draws a laid-out page onto a fresh canvas element. Browser only. */
export function renderDispatchLayoutCanvas(layout: DispatchImageLayout): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  paintDispatchCard(ctx, layout);
  return canvas;
}

/** Draws the order onto a fresh canvas element. Browser only. */
export function renderDispatchCardCanvas(card: DispatchCard): HTMLCanvasElement | null {
  return renderDispatchLayoutCanvas(layoutDispatchCard(card));
}

/** A laid-out page as a PNG blob. */
async function layoutBlob(layout: DispatchImageLayout): Promise<Blob | null> {
  const canvas = renderDispatchLayoutCanvas(layout);
  if (!canvas) return null;
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

/** The order as a PNG blob, ready to be shared or downloaded. */
export async function dispatchCardBlob(card: DispatchCard): Promise<Blob | null> {
  return layoutBlob(layoutDispatchCard(card));
}

/** The same PNG as a `File`, which is what `navigator.share()` accepts. */
export async function dispatchCardImageFile(
  card: DispatchCard,
  fileName: string,
): Promise<File | null> {
  const blob = await dispatchCardBlob(card);
  if (!blob || typeof File === 'undefined') return null;
  return new File([blob], fileName, { type: 'image/png' });
}

/**
 * Every page of the order as a `File`, in reading order — the array that goes
 * to `navigator.share({ files })` in one go, so WhatsApp receives the whole
 * convoy as one set of images rather than as several separate shares.
 *
 * `nameOf` is handed the page so the parts can be numbered; a page that fails
 * to rasterise is dropped rather than sent as a hole in the middle of the order.
 */
export async function dispatchCardImageFiles(
  card: DispatchCard,
  nameOf: (page: DispatchCardPage) => string,
): Promise<File[]> {
  if (typeof File === 'undefined') return [];
  const files: File[] = [];
  for (const page of paginateDispatchCard(card)) {
    const blob = await layoutBlob(layoutDispatchPage(page));
    if (blob) files.push(new File([blob], nameOf(page), { type: 'image/png' }));
  }
  return files;
}
